import React, { useState, useEffect, useContext } from 'react'
import { toast } from 'react-toastify'
import { Store } from '../../Utils/Store'

const CategoryManagement = () => {
  const { state, dispatch } = useContext(Store)
  const [categories, setCategories] = useState(state.Categories || [])
  const [subcategories, setSubcategories] = useState(state.Subcategories || [])
  const [loading, setLoading] = useState(false)
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [editingSubcategory, setEditingSubcategory] = useState(null)
  
  const [categoryForm, setCategoryForm] = useState({ name: '' })
  const [subcategoryForm, setSubcategoryForm] = useState({
    category_id: '',
    name: '',
    route: '',
    content: '',
    image: null,
    imagePreview: null,
    imageData: null // Store base64 image data
  })

  // Sync with Store
  useEffect(() => {
    setCategories(state.Categories || [])
    setSubcategories(state.Subcategories || [])
  }, [state.Categories, state.Subcategories])

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      if (editingCategory) {
        // Update category
        const updatedCategory = {
          ...editingCategory,
          name: categoryForm.name
        }
        dispatch({ type: 'UpdateCategory', payload: updatedCategory })
        toast.success('Category updated!')
      } else {
        // Create new category
        // ID will be generated by Firestore if using it, otherwise use timestamp
        const newCategory = {
          id: Date.now().toString(), // String ID for compatibility
          name: categoryForm.name,
          created_at: new Date().toISOString()
        }
        dispatch({ type: 'AddCategory', payload: newCategory })
        toast.success('Category added!')
      }
      
      setCategoryForm({ name: '' })
      setShowCategoryForm(false)
      setEditingCategory(null)
    } catch (error) {
      console.error('Error saving category:', error)
      toast.error(error.message || 'Failed to save category')
    } finally {
      setLoading(false)
    }
  }

  const handleCategoryDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category? All subcategories will also be deleted.')) {
      return
    }
    try {
      dispatch({ type: 'DeleteCategory', payload: id })
      toast.success('Category deleted!')
    } catch (error) {
      console.error('Error deleting category:', error)
      toast.error(error.message || 'Failed to delete category')
    }
  }

  const handleCategoryEdit = (category) => {
    setEditingCategory(category)
    setCategoryForm({ name: category.name })
    setShowCategoryForm(true)
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Convert to base64 for storage
      const reader = new FileReader()
      reader.onloadend = () => {
        setSubcategoryForm({
          ...subcategoryForm,
          image: file,
          imagePreview: reader.result,
          imageData: reader.result // Store base64 data
        })
      }
      reader.readAsDataURL(file)
    }
  }

  // Subcategory handlers
  const handleSubcategorySubmit = async (e) => {
    e.preventDefault()
    
    // Validate required fields
    if (!subcategoryForm.category_id || subcategoryForm.category_id === '' || subcategoryForm.category_id === '0') {
      toast.error('Please select a category')
      return
    }
    
    if (!subcategoryForm.name || subcategoryForm.name.trim() === '') {
      toast.error('Please enter a subcategory name')
      return
    }
    
    setLoading(true)
    try {
      if (editingSubcategory) {
        // Update subcategory
        const updatedSubcategory = {
          ...editingSubcategory,
          category_id: subcategoryForm.category_id, // Keep as string for Firestore compatibility
          name: subcategoryForm.name.trim(),
          route: subcategoryForm.route ? subcategoryForm.route.trim() : null,
          content: subcategoryForm.content ? subcategoryForm.content.trim() : null,
          image: subcategoryForm.imageData || editingSubcategory.image || null
        }
        dispatch({ type: 'UpdateSubcategory', payload: updatedSubcategory })
        toast.success('Subcategory updated!')
      } else {
        // Create new subcategory
        // ID will be generated by Firestore if using it, otherwise use timestamp
        const newSubcategory = {
          id: Date.now().toString(), // String ID for compatibility
          category_id: subcategoryForm.category_id, // Keep as string for Firestore compatibility
          name: subcategoryForm.name.trim(),
          route: subcategoryForm.route ? subcategoryForm.route.trim() : null,
          content: subcategoryForm.content ? subcategoryForm.content.trim() : null,
          image: subcategoryForm.imageData || null,
          created_at: new Date().toISOString()
        }
        dispatch({ type: 'AddSubcategory', payload: newSubcategory })
        toast.success('Subcategory added!')
      }
      
      setSubcategoryForm({ 
        category_id: '', 
        name: '', 
        route: '', 
        content: '',
        image: null,
        imagePreview: null,
        imageData: null
      })
      setShowSubcategoryForm(false)
      setEditingSubcategory(null)
    } catch (error) {
      console.error('Error saving subcategory:', error)
      toast.error(error.message || 'Failed to save subcategory')
    } finally {
      setLoading(false)
    }
  }

  const handleSubcategoryDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) {
      return
    }
    try {
      dispatch({ type: 'DeleteSubcategory', payload: id })
      toast.success('Subcategory deleted!')
    } catch (error) {
      console.error('Error deleting subcategory:', error)
      toast.error(error.message || 'Failed to delete subcategory')
    }
  }

  const handleSubcategoryEdit = (subcategory) => {
    setEditingSubcategory(subcategory)
    setSubcategoryForm({
      category_id: subcategory.category_id,
      name: subcategory.name,
      route: subcategory.route || '',
      content: subcategory.content || '',
      image: null,
      imagePreview: subcategory.image || null,
      imageData: subcategory.image || null
    })
    setShowSubcategoryForm(true)
  }

  // Get category name for display
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId)
    return category ? category.name : 'Unknown'
  }

  return (
    <div>
      <div style={{ 
        backgroundColor: '#fff', 
        padding: '20px', 
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2 style={{ margin: 0, color: '#333' }}>Category Management</h2>
          <div>
            <button
              onClick={() => {
                setShowCategoryForm(true)
                setEditingCategory(null)
                setCategoryForm({ name: '' })
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              <i className="fa fa-plus" style={{ marginRight: '5px' }}></i>
              Add Category
            </button>
            <button
              onClick={() => {
                setShowSubcategoryForm(true)
                setEditingSubcategory(null)
                setSubcategoryForm({ category_id: '', name: '', route: '', content: '', image: null, imagePreview: null, imageData: null })
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              <i className="fa fa-plus" style={{ marginRight: '5px' }}></i>
              Add Subcategory
            </button>
          </div>
        </div>

        {/* Category Form */}
        {showCategoryForm && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
            <form onSubmit={handleCategorySubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Category Name:
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#007bff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    marginRight: '10px'
                  }}
                >
                  {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryForm(false)
                    setEditingCategory(null)
                    setCategoryForm({ name: '' })
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Subcategory Form */}
        {showSubcategoryForm && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3>{editingSubcategory ? 'Edit Subcategory' : 'Add New Subcategory'}</h3>
            <form onSubmit={handleSubcategorySubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Category: <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  value={subcategoryForm.category_id}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, category_id: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Subcategory Name: <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  value={subcategoryForm.name}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Route (optional):
                </label>
                <input
                  type="text"
                  value={subcategoryForm.route}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, route: e.target.value })}
                  placeholder="/service-detail"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Content (optional):
                </label>
                <textarea
                  value={subcategoryForm.content}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, content: e.target.value })}
                  rows={6}
                  placeholder="Enter content for this subcategory..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Image (optional):
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px'
                  }}
                />
                {subcategoryForm.imagePreview && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={subcategoryForm.imagePreview} 
                      alt="Preview" 
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        borderRadius: '5px',
                        border: '1px solid #ddd',
                        padding: '5px'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSubcategoryForm({
                          ...subcategoryForm,
                          image: null,
                          imagePreview: null,
                          imageData: null
                        })
                      }}
                      style={{
                        marginLeft: '10px',
                        padding: '5px 15px',
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    marginRight: '10px'
                  }}
                >
                  {loading ? 'Saving...' : (editingSubcategory ? 'Update' : 'Save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSubcategoryForm(false)
                    setEditingSubcategory(null)
                    setSubcategoryForm({ 
                      category_id: '', 
                      name: '', 
                      route: '', 
                      content: '',
                      image: null,
                      imagePreview: null,
                      imageData: null
                    })
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Categories List */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '15px', color: '#333' }}>Categories</h3>
          {categories.length === 0 ? (
            <p style={{ color: '#666' }}>No categories yet. Add your first category above.</p>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '15px'
            }}>
              {categories.map(category => (
                <div key={category.id} style={{
                  backgroundColor: '#fff',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{category.name}</h4>
                  <div>
                    <button
                      onClick={() => handleCategoryEdit(category)}
                      style={{
                        padding: '5px 15px',
                        backgroundColor: '#ffc107',
                        color: '#333',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        marginRight: '5px',
                        fontSize: '12px'
                      }}
                    >
                      <i className="fa fa-edit"></i> Edit
                    </button>
                    <button
                      onClick={() => handleCategoryDelete(category.id)}
                      style={{
                        padding: '5px 15px',
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      <i className="fa fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Subcategories List */}
        <div>
          <h3 style={{ marginBottom: '15px', color: '#333' }}>Subcategories</h3>
          {subcategories.length === 0 ? (
            <p style={{ color: '#666' }}>No subcategories yet. Add your first subcategory above.</p>
          ) : (
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #ddd'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Image</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Category</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Subcategory</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Route</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subcategories.map(sub => (
                    <tr key={sub.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px' }}>
                        {sub.image ? (
                          <img 
                            src={sub.image} 
                            alt={sub.name}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '5px',
                              border: '1px solid #ddd'
                            }}
                          />
                        ) : (
                          <span style={{ color: '#999', fontSize: '12px' }}>No image</span>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>{getCategoryName(sub.category_id)}</td>
                      <td style={{ padding: '12px' }}>{sub.name}</td>
                      <td style={{ padding: '12px' }}>{sub.route || '-'}</td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => handleSubcategoryEdit(sub)}
                          style={{
                            padding: '5px 15px',
                            backgroundColor: '#ffc107',
                            color: '#333',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            marginRight: '5px',
                            fontSize: '12px'
                          }}
                        >
                          <i className="fa fa-edit"></i> Edit
                        </button>
                        <button
                          onClick={() => handleSubcategoryDelete(sub.id)}
                          style={{
                            padding: '5px 15px',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          <i className="fa fa-trash"></i> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CategoryManagement
